import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, X, ScanLine, Loader2, AlertCircle, ArrowRight, Keyboard } from 'lucide-react';

const REF_RE = /(REM\d{2,8})/i;

/**
 * Phase 2.4 - Public QR scanner.
 * Reads a QR via the device camera. If the decoded value contains a
 * REM-XXX reference, navigate to /suivi/REM-XXX.
 *
 * Falls back to manual text input when the camera is denied or
 * the QR is not decoded after ~30s.
 */
const QrScannerModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('camera'); // 'camera' | 'manual'
  const [error, setError] = useState(null);
  const [manualRef, setManualRef] = useState('');
  const [starting, setStarting] = useState(false);
  const scannerRef = useRef(null);
  const containerId = 'public-qr-scanner-region';

  const stopScanner = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
        scannerRef.current = null;
      }
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => {
    if (!open) return;
    setPhase('camera');
    setError(null);
    let cancelled = false;
    let fallbackTimer = null;

    (async () => {
      setStarting(true);
      try {
        const qr = new Html5Qrcode(containerId);
        scannerRef.current = qr;
        await qr.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decoded) => {
            if (cancelled) return;
            handleDecoded(decoded);
          },
          (_err) => { /* ignore per-frame failures */ }
        );
        // 30s fallback to manual input if nothing decoded
        fallbackTimer = setTimeout(() => {
          if (!cancelled && phase === 'camera') {
            setError('Aucun QR detecte. Essayez la saisie manuelle.');
          }
        }, 30000);
      } catch (e) {
        if (cancelled) return;
        const name = e?.name || '';
        if (name === 'NotAllowedError' || /permission/i.test(String(e))) {
          setError('Permission camera refusee. Saisissez votre numero ci-dessous.');
        } else {
          setError('Camera indisponible. Saisissez votre numero ci-dessous.');
        }
        setPhase('manual');
      } finally {
        setStarting(false);
      }
    })();

    return () => {
      cancelled = true;
      if (fallbackTimer) clearTimeout(fallbackTimer);
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleDecoded = async (text) => {
    await stopScanner();
    const match = String(text).match(REF_RE);
    if (match && match[1]) {
      const ref = match[1].toUpperCase();
      toast.success('QR detecte, redirection...');
      onClose?.();
      setTimeout(() => navigate(`/suivi/${ref}`), 250);
      return;
    }
    setError('QR non reconnu (format remobiles attendu : REM0123).');
    setPhase('manual');
  };

  const submitManual = (e) => {
    e?.preventDefault?.();
    const m = manualRef.trim().toUpperCase().match(REF_RE);
    if (!m) {
      setError('Format invalide. Exemple : REM0144');
      return;
    }
    toast.success('Reference detectee');
    onClose?.();
    setTimeout(() => navigate(`/suivi/${m[1]}`), 200);
  };

  const handleClose = async () => {
    await stopScanner();
    onClose?.();
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={handleClose}
      data-testid="public-qr-scanner-modal"
    >
      <div className="bg-slate-950 border border-amber-500/40 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
          <h3 className="text-lg font-bold text-amber-300 flex items-center gap-2">
            <ScanLine className="w-5 h-5" /> Suivre ma reparation
          </h3>
          <button type="button" onClick={handleClose} className="text-slate-400 hover:text-white p-1" aria-label="Fermer" data-testid="public-qr-scanner-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {phase === 'camera' && (
            <>
              <p className="text-xs text-slate-400 text-center">
                Pointez la camera vers le QR de votre etiquette.
              </p>
              <div id={containerId} className="rounded-xl overflow-hidden border border-slate-700 bg-black aspect-square mx-auto" data-testid="public-qr-scanner-region" />
              {starting && (
                <p className="text-xs text-slate-400 flex items-center justify-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Activation camera...
                </p>
              )}
              {error && (
                <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 flex items-center gap-2">
                  <AlertCircle className="w-3 h-3" /> {error}
                </div>
              )}
              <button type="button" onClick={() => { setPhase('manual'); setError(null); stopScanner(); }} className="w-full text-sm text-slate-300 hover:text-white flex items-center justify-center gap-1 py-2" data-testid="public-qr-scanner-manual-btn">
                <Keyboard className="w-4 h-4" /> Saisir manuellement
              </button>
            </>
          )}

          {phase === 'manual' && (
            <form onSubmit={submitManual} className="space-y-3" data-testid="public-qr-scanner-manual-form">
              <p className="text-sm text-slate-300">
                Entrez votre numero de reparation (ex : <span className="font-mono text-amber-300">REM0144</span>)
              </p>
              <input
                type="text"
                value={manualRef}
                onChange={(e) => setManualRef(e.target.value)}
                placeholder="REM0144"
                autoFocus
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-3 text-white font-mono uppercase tracking-wider focus:outline-none focus:border-amber-400"
                data-testid="public-qr-scanner-manual-input"
              />
              {error && (
                <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2 flex items-center gap-2">
                  <AlertCircle className="w-3 h-3" /> {error}
                </div>
              )}
              <div className="flex gap-2">
                <button type="button" onClick={() => { setPhase('camera'); setError(null); }} className="flex-1 px-4 py-2 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm flex items-center justify-center gap-1" data-testid="public-qr-scanner-camera-btn">
                  <Camera className="w-4 h-4" /> Camera
                </button>
                <button type="submit" className="flex-1 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm flex items-center justify-center gap-1" data-testid="public-qr-scanner-submit-btn">
                  Suivre <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QrScannerModal;
